<template>
  <v-navigation-drawer
    v-model="isOpen"
    class="app-navigation bg-background d-print-none pl-4"
    :class="{
      'hidden': !isOpen && !mobile,
    }"
    floating
    name="app-navigation"
    role="navigation"
    :touchless
    :width
  >
    <template v-if="slots.prepend" #prepend>
      <slot name="prepend"></slot>
    </template>

    <slot>
      <v-list v-model="navigation" :class="listClass" density="compact" nav open-strategy="single">
        <template v-for="route in routes" :key="route.path">
          <app-navigation-item
            v-if="!route.children || route.children.every((child) => isItemHidden(child))"
            :append-icon="getIconsFromRoute(route).appendIcon"
            :icon="getIconsFromRoute(route).icon"
            :route
          />
          <app-navigation-list-group v-else :route />
        </template>
      </v-list>
      <slot name="append.default"></slot>
    </slot>

    <template v-if="slots.append" #append>
      <slot name="append"></slot>
    </template>
  </v-navigation-drawer>
</template>

<script lang="ts" setup>
import { type ComputedRef, type MaybeRefOrGetter, ref, toRef, useSlots } from 'vue';
import { type RouteMeta, type RouteRecordRaw } from 'vue-router';
import { useDisplay } from 'vuetify';

import { provideAppNavigation } from './AppNavigation.context';
import AppNavigationItem from './AppNavigationItem.vue';
import AppNavigationListGroup from './AppNavigationListGroup.vue';

interface Props {
  appendIconMap?: Map<string, string | undefined>;
  hiddenRouteMap?: Map<string, boolean>;
  iconMap?: Map<string, string>;
  isRoutePermitted?: (meta?: MaybeRefOrGetter<RouteMeta | undefined>) => ComputedRef<boolean>;
  listClass?: any;
  modelValue?: boolean;
  routes?: RouteRecordRaw[];
  touchless?: boolean;
  width?: number;
}

const props = withDefaults(defineProps<Props>(), {
  isRoutePermitted: undefined,
  listClass: '',
  modelValue: true,
  width: 270,
  appendIconMap: () => new Map(),
  hiddenRouteMap: () => new Map(),
  iconMap: () => new Map(),
  routes: () => [],
});

const isOpen = defineModel<boolean>({ default: true });

const iconMapRef = toRef(props, 'iconMap');
const appendIconMapRef = toRef(props, 'appendIconMap');
const hiddenRoutemapRef = toRef(props, 'hiddenRouteMap');

const { getIconsFromRoute, isItemHidden } = provideAppNavigation(
  iconMapRef,
  appendIconMapRef,
  hiddenRoutemapRef,
  () => props.isRoutePermitted,
);

const navigation = ref([]);

const { mobile } = useDisplay();

const slots = useSlots();
</script>

<style lang="scss" scoped>
:deep(.v-list-group .v-list-group--prepend){
  --parent-padding: calc(3rem);
}

.app-navigation{
  opacity: 1;
  visibility: visible;

  transition: var(--bootable-transition);

  &.hidden{
    opacity: 0;
    visibility: hidden;
  }
}
</style>
